import classNames from 'classnames'
import {formatDistance, parseISO} from 'date-fns'
import React, {useEffect} from 'react'

import {subscribeOnBuild} from '../../../../../../actions/builds'
import Routes, {getHref} from '../../../../../../routes'
import {restApi} from '../../../../../../services/rest'
import type {BuildId} from '../../../../../../types'
import {stringifyBranch} from '../../../../../../utils/branchNames'
import {useDisplayDate} from '../../../../../../utils/dateTimeUtils'
import {objectToQuery} from '../../../../../../utils/queryParams'
import BuildDurationAnchor from '../../../../../common/BuildDuration/BuildDurationAnchor/BuildDurationAnchor.container'
import {formatBuildNumber} from '../../../../../common/BuildNumber/BuildNumber.utils'
import RouterLink from '../../../../../common/RouterLink/RouterLink'
import StopBuild from '../../../../../common/StopBuild/StopBuild.container'
import BuildChangesDropdown from '../../../../../packages/Changes/ChangesDropdown/BuildChangesDropdown.container'
import PipelineBranchLabel from '../../../components/common/PipelineBranchLabel/PipelineBranchLabel'
import RunStatus from '../../../components/common/RunStatus/RunStatus'
import type {pipelineHeadFragment$key} from '../../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineId} from '../../../hooks/pipeline'
import {usePipelineHistoryArg} from '../../PipelinePage.hooks'

import styles from './PipelineRunLine.css'

import {useAppDispatch} from 'src/hooks/react-redux'

type Props = {
  runId: BuildId
  pipelineHeadKey: pipelineHeadFragment$key | null
}

export default function PipelineRunLine({runId, pipelineHeadKey}: Props) {
  const id = usePipelineId()
  const arg = usePipelineHistoryArg(pipelineHeadKey)
  const {run} = restApi.endpoints.getAllBuildsNormalized.useQuery(arg, {
    selectFromResult: ({data}) => ({run: data?.entities.builds?.[runId]}),
  })
  const {
    number,
    state,
    startEstimate,
    finishEstimate,
    startDate,
    finishDate,
    branchName,
    defaultBranch,
  } = run ?? {}
  const isQueued = state === 'queued'
  const href = getHref(Routes.PIPELINE_RUN, {id, runId: String(runId)})
  const startDateFormatted = useDisplayDate(startDate, true)
  const startEstimateFormatted = useDisplayDate(startEstimate, true)
  const dispatch = useAppDispatch()
  useEffect(() => dispatch(subscribeOnBuild(runId)))

  return (
    <>
      <div className={styles.cell}>
        {!isQueued && (
          <RouterLink to={href} className={styles.number}>
            {formatBuildNumber({number}).text}
          </RouterLink>
        )}
      </div>
      <div className={styles.cell}>
        <RunStatus {...run} className={styles.status} />
      </div>
      <div className={styles.cell}>
        {branchName != null && (
          <PipelineBranchLabel
            name={branchName}
            main={defaultBranch}
            className={classNames({
              [styles.branchLabel]: defaultBranch,
            })}
            to={{
              search: objectToQuery({
                branch: stringifyBranch({default: defaultBranch, name: branchName}),
              }),
            }}
          />
        )}
      </div>
      <div className={styles.cell}>
        <BuildChangesDropdown buildId={runId} />
      </div>
      <div className={classNames(styles.cell, styles.startDate)}>
        {isQueued ? (
          startEstimate != null ? (
            <span className={styles.estimate}>{`~${startEstimateFormatted}`}</span>
          ) : null
        ) : (
          startDateFormatted
        )}
      </div>
      <div className={classNames(styles.cell, styles.duration)}>
        {isQueued ? (
          startEstimate != null && finishEstimate != null ? (
            <span className={styles.estimate}>
              {`~${formatDistance(parseISO(startEstimate), parseISO(finishEstimate))}`}
            </span>
          ) : null
        ) : state === 'running' ? (
          <>
            <BuildDurationAnchor buildId={runId} />
            <StopBuild className={styles.stop} buildId={runId} />
          </>
        ) : startDate != null && finishDate != null ? (
          formatDistance(parseISO(startDate), parseISO(finishDate))
        ) : null}
      </div>
    </>
  )
}
